import { slugs } from '@/constants'
import { GuildPermissions } from '@/services/GuildUserService'
import { inTheaters, isSellingTickets, isStreaming } from '@/services/PhaseManagerService'
import { Episode, Project, VideoMetaData } from '@/services/ProjectsService'
import { isAClip, isALivestream, isAPitch, isAPreview, isASneakPeek, isATeaser, isATorch } from '@/utils/EpisodeUtil'
import { TranslateFunction } from '@/utils/translate/translate-client'

const slugsToHideEpisodeNumbers = [slugs.lightwise, slugs.dryBar, slugs.tuttleTwinsMinisodes]

type CtaValues = {
  primaryCtaPath: string
  primaryCtaText: string
  primaryCtaIcon: string
  secondaryCtaPath: string
  secondaryCtaText: string
  secondaryCtaIcon: string
}

type WatchProjectCTAResult = {
  primaryCtaPath: string | null
  primaryCtaText: string | null
  primaryCtaIcon: string | null
  secondaryCtaPath: string | null
  secondaryCtaText: string | null
  secondaryCtaIcon: string | null
  trailer: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null
}

type BaseCtaParams = {
  projectData: Project
  t: TranslateFunction
  defaultCtas: CtaValues
  sneakPeek?: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null
  trailer?: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null
}

type UserParams = {
  isGuildMember: boolean
  isGuildMemberContent: boolean
  guildPermissions: GuildPermissions
  watchableReasons?: string[]
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
}

const getProjectStatus = (projectData: Project, movie?: Episode) => {
  const now = new Date()
  const isMovie = projectData?.projectType === 'movie'
  const currentlyInTheaters = inTheaters(projectData?.primaryFlowPhases ?? [])
  const sellingTickets = isSellingTickets(projectData?.primaryFlowPhases ?? [])
  const currentlyStreaming = isStreaming(projectData?.primaryFlowPhases ?? [])

  const isMovieAvailableToStream =
    (isMovie && movie?.earlyAccessDate && new Date(movie.earlyAccessDate) < now) ||
    (isMovie && movie?.publiclyAvailableDate && new Date(movie.publiclyAvailableDate) < now)

  return {
    isMovie,
    currentlyInTheaters,
    sellingTickets,
    currentlyStreaming: Boolean(currentlyStreaming || isMovieAvailableToStream),
  }
}

const getWatchableContent = (projectData: Project, bonusVideos?: VideoMetaData[]) => {
  return {
    trailer: findTrailer(projectData, bonusVideos),
    sneakPeek: findSneakPeek(projectData, bonusVideos),
    watchableEpisode: findFirstWatchableEpisode(projectData, bonusVideos),
    movie: projectData?.seasons?.[0]?.episodes?.find((episode) => episode?.name?.toLowerCase()?.includes('movie')),
  }
}

const getContinueWatchingInfo = (projectData: Project) => {
  return {
    lastWatched: projectData?.continueWatching?.guid,
    continueWatchingSeasonNumber: projectData?.continueWatching?.season?.seasonNumber,
    continueWatchingEpisodeNumber: projectData?.continueWatching?.episodeNumber,
    hideEpisodeNumbers: Boolean(projectData?.slug && slugsToHideEpisodeNumbers.includes(projectData.slug)),
  }
}

const createDefaultCtas = (ctaPath?: string, ctaText?: string): CtaValues => {
  return {
    primaryCtaPath: ctaPath ?? '',
    primaryCtaText: ctaText ?? '',
    primaryCtaIcon: 'play',
    secondaryCtaPath: '',
    secondaryCtaText: '',
    secondaryCtaIcon: '',
  }
}

const formatCtaResult = (
  defaultCtas: CtaValues,
  trailer: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null,
): WatchProjectCTAResult => {
  return {
    primaryCtaPath: defaultCtas.primaryCtaPath === '' ? null : defaultCtas.primaryCtaPath || null,
    primaryCtaText: defaultCtas.primaryCtaText === '' ? null : defaultCtas.primaryCtaText || null,
    primaryCtaIcon: defaultCtas.primaryCtaIcon === '' ? null : defaultCtas.primaryCtaIcon || null,
    secondaryCtaPath: defaultCtas.secondaryCtaPath === '' ? null : defaultCtas.secondaryCtaPath || null,
    secondaryCtaText: defaultCtas.secondaryCtaText === '' ? null : defaultCtas.secondaryCtaText || null,
    secondaryCtaIcon: defaultCtas.secondaryCtaIcon === '' ? null : defaultCtas.secondaryCtaIcon || null,
    trailer,
  }
}

// Handle movie that is currently streaming
const handleStreamingMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  movie,
  isGuildMemberContent,
  isGuildMember,
  watchableReasons,
}: BaseCtaParams & {
  lastWatched?: string
  movie?: Episode
  isGuildMemberContent: boolean
  isGuildMember: boolean
  watchableReasons?: string[]
}): CtaValues => {
  if (isGuildMemberContent && !(isGuildMember || (!isGuildMember && watchableReasons?.length))) {
    defaultCtas.primaryCtaText = t('joinToWatch', 'Join To Watch')
    defaultCtas.primaryCtaPath = `/guild/join/${projectData.slug}`
    defaultCtas.primaryCtaIcon = 'guild'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })

    return defaultCtas
  }
  if (lastWatched) {
    defaultCtas.primaryCtaText = t('resume', 'Resume')
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'
  } else {
    defaultCtas.primaryCtaText = t('watchNow', 'Watch Now')
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${movie?.guid}`
    defaultCtas.primaryCtaIcon = 'play'
  }

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    defaultTrailer: true,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie that is in theaters
const handleInTheatersMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  guildPermissions,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  guildPermissions: GuildPermissions
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
}): CtaValues => {
  if (isGuildMember && guildPermissions.hasFreeTickets && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
  } else {
    defaultCtas.primaryCtaText = t('getShowtimes', 'Get Showtimes')
  }

  defaultCtas.primaryCtaPath = `/tickets/${projectData.slug}`
  defaultCtas.primaryCtaIcon = 'ticket'

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie when tickets are being sold (but not in theaters yet)
const handleSellingTicketsMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  activeSweepstake?: boolean
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
  sellingTickets?: boolean
}): CtaValues => {
  const basePath = `/tickets/${projectData.slug}`

  if (isGuildMember && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
    defaultCtas.primaryCtaIcon = 'guild'
  } else {
    defaultCtas.primaryCtaText = t('getShowtimes', 'Get Showtimes')
    defaultCtas.primaryCtaIcon = 'ticket'
  }

  defaultCtas.primaryCtaPath = basePath

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

// Handle movie that has giveaway
const handleGiveawayTicketsMovie = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  isGuildMember,
  areGuildTicketsAvailable,
  guildCodes,
}: BaseCtaParams & {
  isGuildMember: boolean
  activeSweepstake?: boolean
  areGuildTicketsAvailable?: boolean
  guildCodes?: string[]
  sellingTickets?: boolean
}): CtaValues => {
  const basePath = `/giveaway/${projectData.slug}`

  if (isGuildMember && areGuildTicketsAvailable && guildCodes?.length) {
    defaultCtas.primaryCtaText = t('redeemGuildTickets', 'Redeem Guild Tickets')
    defaultCtas.primaryCtaIcon = 'guild'
  } else {
    defaultCtas.primaryCtaText = t('preOrderTickets', 'Pre-Order Tickets')
    defaultCtas.primaryCtaIcon = 'ticket'
  }

  defaultCtas.primaryCtaPath = basePath

  getPreviewContentCta({
    isPrimary: false,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })

  return defaultCtas
}

const getSeriesResumeText = ({
  t,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  hideEpisodeNumbers,
}: {
  t: TranslateFunction
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  hideEpisodeNumbers: boolean
}): string => {
  return continueWatchingSeasonNumber && continueWatchingEpisodeNumber && !hideEpisodeNumbers
    ? t('resumeEpisodeSeason', `Resume S{{ seasonNumber }} E{{ episodeNumber}}`, {
        seasonNumber: continueWatchingSeasonNumber,
        episodeNumber: continueWatchingEpisodeNumber,
      })
    : t('resume', 'Resume')
}

const getSeriesEpisodeText = ({
  t,
  watchableEpisode,
  hideEpisodeNumbers,
}: {
  t: TranslateFunction
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
}): string => {
  return watchableEpisode &&
    'seasonNumber' in watchableEpisode &&
    'episodeNumber' in watchableEpisode &&
    watchableEpisode?.episodeNumber &&
    watchableEpisode.seasonNumber &&
    !hideEpisodeNumbers
    ? t('watchEpisodeSeason', `Watch S{{ seasonNumber }} E{{ episodeNumber}}`, {
        seasonNumber: watchableEpisode.seasonNumber,
        episodeNumber: watchableEpisode.episodeNumber,
      })
    : t('watchNow', 'Watch Now')
}

// Handle streaming series with Guild Member content
const handleGuildMemberStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  isGuildMember,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
}: BaseCtaParams & {
  lastWatched?: string
  isGuildMember: boolean
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
}): CtaValues => {
  if (isGuildMember) {
    if (lastWatched) {
      defaultCtas.primaryCtaText = getSeriesResumeText({
        t,
        continueWatchingSeasonNumber,
        continueWatchingEpisodeNumber,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
      defaultCtas.primaryCtaIcon = 'play'

      getPreviewContentCta({
        isPrimary: false,
        projectData,
        t,
        defaultCtas,
        sneakPeek,
        trailer,
        defaultTrailer: true,
        isGuildMember,
      })
    } else if (watchableEpisode) {
      defaultCtas.primaryCtaText = getSeriesEpisodeText({
        t,
        watchableEpisode,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
      defaultCtas.primaryCtaIcon = 'play'

      getPreviewContentCta({
        isPrimary: false,
        projectData,
        t,
        defaultCtas,
        sneakPeek,
        trailer,
        defaultTrailer: true,
        isGuildMember,
      })
    } else {
      defaultCtas.primaryCtaText = ''
      defaultCtas.primaryCtaPath = ''
      defaultCtas.primaryCtaIcon = ''
    }
  } else {
    handleNonGuildMemberStreamingSeries({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched,
      watchableEpisode,
      hideEpisodeNumbers,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      isGuildMember,
    })
  }

  return defaultCtas
}

// Handle streaming series for non-Guild members
const handleNonGuildMemberStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  isGuildMember,
}: BaseCtaParams & {
  lastWatched?: string
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  isGuildMember?: boolean
}): CtaValues => {
  if (lastWatched) {
    defaultCtas.primaryCtaText = getSeriesResumeText({
      t,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  } else if (watchableEpisode) {
    const isPubliclyAvailable =
      'publiclyAvailableDate' in watchableEpisode &&
      watchableEpisode?.publiclyAvailableDate &&
      new Date(watchableEpisode.publiclyAvailableDate) < new Date()

    if (isPubliclyAvailable) {
      defaultCtas.primaryCtaText = getSeriesEpisodeText({
        t,
        watchableEpisode,
        hideEpisodeNumbers,
      })
      defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
      defaultCtas.primaryCtaIcon = 'play'
    } else {
      defaultCtas.primaryCtaText = t('joinToWatch', 'Join To Watch')
      defaultCtas.primaryCtaPath = `/guild/join/${projectData.slug}`
      defaultCtas.primaryCtaIcon = 'guild'
    }

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })
  } else {
    defaultCtas.primaryCtaText = ''
    defaultCtas.primaryCtaPath = ''
    defaultCtas.primaryCtaIcon = ''
  }

  return defaultCtas
}

// Handle publicly available streaming series
const handlePublicStreamingSeries = ({
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  lastWatched,
  watchableEpisode,
  hideEpisodeNumbers,
  continueWatchingSeasonNumber,
  continueWatchingEpisodeNumber,
  isGuildMember,
}: BaseCtaParams & {
  lastWatched?: string
  watchableEpisode?: Episode | VideoMetaData | null
  hideEpisodeNumbers: boolean
  continueWatchingSeasonNumber?: number
  continueWatchingEpisodeNumber?: number
  isGuildMember?: boolean
}): CtaValues => {
  if (lastWatched) {
    defaultCtas.primaryCtaText = getSeriesResumeText({
      t,
      continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${lastWatched}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  } else if (watchableEpisode) {
    defaultCtas.primaryCtaText = getSeriesEpisodeText({
      t,
      watchableEpisode,
      hideEpisodeNumbers,
    })
    defaultCtas.primaryCtaPath = `/watch/${projectData.slug}/episode/${watchableEpisode?.guid}`
    defaultCtas.primaryCtaIcon = 'play'

    getPreviewContentCta({
      isPrimary: false,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
    })
  } else {
    defaultCtas.primaryCtaText = ''
    defaultCtas.primaryCtaPath = ''
    defaultCtas.primaryCtaIcon = ''
  }

  return defaultCtas
}

const handleSeriesContent = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  {
    sneakPeek,
    trailer,
    watchableEpisode,
  }: Pick<ReturnType<typeof getWatchableContent>, 'sneakPeek' | 'trailer' | 'watchableEpisode'>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  { isGuildMember, isGuildMemberContent }: Pick<UserParams, 'isGuildMember' | 'isGuildMemberContent'>,
  isStreaming: boolean,
) => {
  // Non-streaming series shows preview content
  if (!isStreaming) {
    return getPreviewContentCta({
      isPrimary: true,
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      defaultTrailer: true,
      isGuildMember,
    })
  }

  // Handle streaming series based on content type
  if (isGuildMemberContent) {
    return handleGuildMemberStreamingSeries({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched: continueWatchingInfo.lastWatched,
      isGuildMember,
      watchableEpisode,
      hideEpisodeNumbers: continueWatchingInfo.hideEpisodeNumbers,
      continueWatchingSeasonNumber: continueWatchingInfo.continueWatchingSeasonNumber,
      continueWatchingEpisodeNumber: continueWatchingInfo.continueWatchingEpisodeNumber,
    })
  }

  return handlePublicStreamingSeries({
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    lastWatched: continueWatchingInfo.lastWatched,
    watchableEpisode,
    hideEpisodeNumbers: continueWatchingInfo.hideEpisodeNumbers,
    continueWatchingSeasonNumber: continueWatchingInfo.continueWatchingSeasonNumber,
    continueWatchingEpisodeNumber: continueWatchingInfo.continueWatchingEpisodeNumber,
    isGuildMember,
  })
}

// Handle movie based on phase status
const handleMovieContent = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  status: ReturnType<typeof getProjectStatus>,
  watchableContent: ReturnType<typeof getWatchableContent>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  userParams: UserParams,
) => {
  const { currentlyInTheaters, sellingTickets, currentlyStreaming } = status
  const { sneakPeek, trailer, movie } = watchableContent
  const { lastWatched } = continueWatchingInfo
  const {
    isGuildMember,
    isGuildMemberContent,
    guildPermissions,
    watchableReasons,
    guildCodes,
    areGuildTicketsAvailable,
    activeSweepstake,
  } = userParams

  // Check if movie can be streamed
  const isStreamable = currentlyStreaming || Boolean(movie?.guid && watchableReasons?.length)

  if (isStreamable) {
    return handleStreamingMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      lastWatched,
      movie,
      isGuildMemberContent,
      isGuildMember,
      watchableReasons,
    })
  }

  if (currentlyInTheaters) {
    return handleInTheatersMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      guildPermissions,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  if (sellingTickets) {
    return handleSellingTicketsMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  if (activeSweepstake) {
    return handleGiveawayTicketsMovie({
      projectData,
      t,
      defaultCtas,
      sneakPeek,
      trailer,
      isGuildMember,
      areGuildTicketsAvailable,
      guildCodes,
    })
  }

  return getPreviewContentCta({
    isPrimary: true,
    projectData,
    t,
    defaultCtas,
    sneakPeek,
    trailer,
    isGuildMember,
  })
}

const handleMovieByStatus = (
  projectData: Project,
  t: TranslateFunction,
  defaultCtas: CtaValues,
  projectStatus: ReturnType<typeof getProjectStatus>,
  watchableContent: ReturnType<typeof getWatchableContent>,
  continueWatchingInfo: ReturnType<typeof getContinueWatchingInfo>,
  userParams: UserParams,
) => {
  const { isMovie, currentlyStreaming } = projectStatus

  if (!isMovie) {
    return handleSeriesContent(
      projectData,
      t,
      defaultCtas,
      watchableContent,
      continueWatchingInfo,
      userParams,
      currentlyStreaming,
    )
  }

  return handleMovieContent(
    projectData,
    t,
    defaultCtas,
    projectStatus,
    watchableContent,
    continueWatchingInfo,
    userParams,
  )
}

export const useWatchProjectCTAs = ({
  t,
  projectData,
  bonusVideos,
  isGuildMember,
  ctaPath,
  ctaText,
  isGuildMemberContent,
  guildPermissions,
  watchableReasons,
  guildCodes,
  areGuildTicketsAvailable,
  activeSweepstake,
  isGeoBlockedProject,
}: {
  t: TranslateFunction
  projectData: Project
  bonusVideos?: VideoMetaData[]
  isGuildMember: boolean
  ctaPath?: string
  ctaText?: string
  isGuildMemberContent: boolean
  guildPermissions: GuildPermissions
  watchableReasons?: string[]
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
  isGeoBlockedProject?: boolean
}): WatchProjectCTAResult => {
  if (isGeoBlockedProject) {
    return {
      primaryCtaText: t('unavailableForLocation', 'Unavailable For Location'),
      primaryCtaPath: 'disabled',
      primaryCtaIcon: 'geo',
      secondaryCtaPath: null,
      secondaryCtaText: null,
      secondaryCtaIcon: null,
      trailer: null,
    }
  }

  const watchableContent = getWatchableContent(projectData, bonusVideos)
  const projectStatus = getProjectStatus(projectData, watchableContent?.movie)
  const continueWatchingInfo = getContinueWatchingInfo(projectData)
  const defaultCtas = createDefaultCtas(ctaPath, ctaText)

  const userParams = {
    isGuildMember,
    isGuildMemberContent,
    guildPermissions,
    watchableReasons,
    guildCodes,
    areGuildTicketsAvailable,
    activeSweepstake,
  }

  handleMovieByStatus(projectData, t, defaultCtas, projectStatus, watchableContent, continueWatchingInfo, userParams)

  return formatCtaResult(defaultCtas, watchableContent.trailer)
}

const isSpecialEpisodeType = (episode: Episode): boolean => {
  return (
    episode?.isTrailer ||
    isATeaser(episode) ||
    isASneakPeek(episode) ||
    isATorch(episode) ||
    isAPitch(episode) ||
    isALivestream(episode) ||
    isAPreview(episode) ||
    isAClip(episode)
  )
}

const findTrailer = (projectData: Project, bonusVideos?: VideoMetaData[]) => {
  // Look for episodes marked as trailer
  const trailer = projectData?.seasons?.[0]?.episodes?.find((episode) => episode?.isTrailer)
  if (trailer) return { ...trailer, isBonus: false }

  //  Look for episodes that are teasers
  const teaser = projectData?.seasons?.[0]?.episodes?.find((episode) => isATeaser(episode))
  if (teaser) return { ...teaser, isBonus: false }

  // Look for episodes with "trailer" in the name
  const namedTrailer = projectData?.seasons?.[0]?.episodes?.find((episode) =>
    episode?.name?.toLowerCase().includes('trailer'),
  )
  if (namedTrailer) return { ...namedTrailer, isBonus: false }

  // Look in bonus videos for trailers or teasers
  if (bonusVideos && bonusVideos.length > 0) {
    const bonusNamedTrailer = bonusVideos.find(
      (video) => video.title?.toLowerCase().includes('trailer') || video.title?.toLowerCase().includes('teaser'),
    )
    if (bonusNamedTrailer) return { ...bonusNamedTrailer, isBonus: true }
  }

  return null
}

const findSneakPeek = (projectData: Project, bonusVideos?: VideoMetaData[]) => {
  // Look for episodes with "sneak peek" in the name
  const namedSneakPeek = projectData?.seasons?.[0]?.episodes?.find((episode) => isASneakPeek(episode))
  if (namedSneakPeek) return { ...namedSneakPeek, isBonus: false }

  // Look in bonus videos for sneak peeks
  if (bonusVideos && bonusVideos.length > 0) {
    const bonusNamedSneakPeek = bonusVideos.find(
      (video) => video.title?.toLowerCase().includes('sneak peek') || video.title?.toLowerCase().includes('sneakpeek'),
    )
    if (bonusNamedSneakPeek) return { ...bonusNamedSneakPeek, isBonus: true }
  }

  return null
}

const getRegularBonusVideos = (bonusVideos: VideoMetaData[]): VideoMetaData[] => {
  return bonusVideos.filter((video) => {
    const lowerTitle = video.title?.toLowerCase() || ''
    return !(lowerTitle.includes('trailer') || lowerTitle.includes('teaser') || lowerTitle.includes('sneak peek'))
  })
}

const findFirstWatchableEpisode = (projectData: Project, bonusVideos?: VideoMetaData[]) => {
  const episodes = projectData?.seasons?.[0]?.episodes
  if (!episodes || episodes.length === 0) return null

  // Filter out special episode types
  const watchableEpisodes = episodes.filter((episode) => !isSpecialEpisodeType(episode) && Boolean(episode?.guid))

  // Sort episodes by number
  const sortedEpisodes = [...watchableEpisodes].sort((a, b) => {
    if (a.episodeNumber && b.episodeNumber) {
      return a.episodeNumber - b.episodeNumber
    }
    return 0
  })

  // If no continue watching data exists, check for watched episodes
  if (!projectData?.continueWatching && sortedEpisodes.length > 0) {
    const nextEpisode = findNextEpisode(sortedEpisodes)
    if (nextEpisode) return { ...nextEpisode, isBonus: false }
  }

  // Default to first episode
  if (sortedEpisodes.length > 0) {
    return { ...sortedEpisodes[0], isBonus: false }
  }

  return findFallbackBonusVideo(bonusVideos)
}

const findNextEpisode = (sortedEpisodes: Episode[]) => {
  // Find episodes with watch position
  const watchedEpisodes = sortedEpisodes.filter(
    (episode) => episode?.watchPosition?.position && episode.watchPosition.position > 0,
  )

  if (watchedEpisodes.length > 0) {
    // Get latest watched episode by episode number
    const latestWatchedEpisode = [...watchedEpisodes].sort((a, b) => {
      if (a.episodeNumber && b.episodeNumber) {
        return b.episodeNumber - a.episodeNumber
      }
      return 0
    })[0]

    // Find the next episode after the latest watched one
    for (const episode of sortedEpisodes) {
      if (
        episode.episodeNumber &&
        latestWatchedEpisode.episodeNumber &&
        episode.episodeNumber > latestWatchedEpisode.episodeNumber
      ) {
        return episode
      }
    }
  }

  return null
}

const findFallbackBonusVideo = (bonusVideos?: VideoMetaData[]) => {
  if (bonusVideos && bonusVideos.length > 0) {
    const regularBonusVideos = getRegularBonusVideos(bonusVideos)
    if (regularBonusVideos.length > 0) {
      return { ...regularBonusVideos[0], isBonus: true }
    }
  }
  return null
}

type getPreviewContentCtaParams = {
  isPrimary: boolean
  projectData: Project
  t: TranslateFunction
  defaultCtas: {
    primaryCtaPath: string
    primaryCtaText: string
    primaryCtaIcon: string
    secondaryCtaPath: string
    secondaryCtaText: string
    secondaryCtaIcon: string
  }
  sneakPeek?: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null
  trailer?: ((VideoMetaData | Episode) & { isBonus?: boolean }) | null
  defaultTrailer?: boolean
  isGuildMember?: boolean
}

const getPreviewContentCta = ({
  isPrimary,
  projectData,
  t,
  defaultCtas,
  sneakPeek,
  trailer,
  defaultTrailer = false,
  isGuildMember = false,
}: getPreviewContentCtaParams) => {
  const content = defaultTrailer ? trailer : sneakPeek || trailer

  if (!content) return defaultCtas

  const contentPath = content.isBonus
    ? `/watch/${projectData.slug}/videos/bonus/${content.guid}`
    : `/watch/${projectData.slug}/episode/${content.guid}`

  let contentText

  if (defaultTrailer && trailer && content === trailer) {
    contentText = t('watchTrailer', 'Watch Trailer')
  } else if (!defaultTrailer && sneakPeek && content === sneakPeek && !isGuildMember && !isPrimary) {
    contentText = t('watchSneakPeek', 'Watch Sneak Peek')
  } else {
    if (!isPrimary && isGuildMember && content === sneakPeek) {
      return defaultCtas
    }
    contentText = content === trailer ? t('watchTrailer', 'Watch Trailer') : t('watchSneakPeek', 'Watch Sneak Peek')
  }

  const contentIcon = 'play'

  if (isPrimary) {
    defaultCtas.primaryCtaPath = contentPath
    defaultCtas.primaryCtaText = contentText
    defaultCtas.primaryCtaIcon = contentIcon
  } else {
    defaultCtas.secondaryCtaPath = contentPath
    defaultCtas.secondaryCtaText = contentText
    defaultCtas.secondaryCtaIcon = contentIcon
  }

  return defaultCtas
}
